import React, { useState, useCallback, useRef, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Webcam from "react-webcam";
import axios from "axios";
import { notifyError } from "MyComponents/Toaster";
import { SpinnerBox } from "MyComponents/Spinner";
import config from "config";
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTranslation } from "react-i18next";
import { isBase64WithinSizeLimit } from 'Utils/fileValidation';
import { compressBase64Image } from 'Utils/imageCompression';
import { cameraErrorKey, listVideoInputs, requestCameraStream } from 'Utils/camera';

const documentOCR_baseURL = config.NODE_API_BASE_URL + "/idAnalyzer/documentOCR";

// Documents default to the rear-facing camera on mobile.
const DEFAULT_FACING_MODE: "user" | "environment" = "environment";

type MyComponentProps = {
  videoConstraints?: any;
  show: boolean;
  handleClose: () => void;
  title: string;
  side: string;
  updateSrc: (srcString: any) => void;
};

const CameraModal = React.forwardRef<any, MyComponentProps>((props, ref) => {
  const { t } = useTranslation();
  const { show, handleClose, title, side, updateSrc, videoConstraints } = props;

  const [isCaptureEnable, setCaptureEnable] = useState<boolean>(false);
  const webcamRef = useRef<Webcam>(null);
  const [imageStatus, setImageStatus] = useState<string | null>(null);
  const [ocrStatus, setOcrStatus] = useState<boolean>(false);
  const [msgToDisplay, setMsgToDisplay] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(DEFAULT_FACING_MODE);
  const [loading, setLoading] = useState<boolean>(false);
  const [cardDetected, setCardDetected] = useState<boolean>(false);
  const [, setStableCounter] = useState<number>(0);
  const [autoCaptureEnabled, setAutoCaptureEnabled] = useState<boolean>(true);
  const [hasMultipleCameras, setHasMultipleCameras] = useState<boolean>(false);
  const [cameraErrorMsgKey, setCameraErrorMsgKey] = useState<string | null>(null);

  const countrySelected = useSelector((state: RootState) => state.auth.selectedCountryName);
  const theme = useSelector((state: RootState) => state.theme.theme);

  useEffect(() => {
    if (show) {
      listVideoInputs().then((videoDevices) => setHasMultipleCameras(videoDevices.length > 1));
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      setCameraErrorMsgKey(null);
      requestCameraStream({ video: { facingMode: DEFAULT_FACING_MODE }, audio: false })
        .then((s) => {
          // Only a permission probe: phones refuse to hand the same camera to
          // <Webcam> while this stream holds it, so release it right away.
          s.getTracks().forEach((track) => track.stop());
          setCaptureEnable(true);
        })
        .catch((error) => {
          console.error("getUserMedia error:", error);
          setCameraErrorMsgKey(cameraErrorKey(error));
        });
    }
    return () => {
      setCaptureEnable(false);
    };
  }, [show]);

  // Unmounting <Webcam> (isCaptureEnable false) is what releases the camera.
  const closeCamera = () => {
    setCaptureEnable(false);
    setStableCounter(0);
    setCardDetected(false);
  };

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setOcrStatus(false);
      setImageStatus(imageSrc);
    }
  }, [webcamRef]);

  const toggleFacingMode = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const detectCard = useCallback(() => {
    const cv = (window as any).cv;
    if (!cv || !cv.imread || !webcamRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;

    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      let srcMat = cv.imread(canvas);
      let gray = new cv.Mat();
      cv.cvtColor(srcMat, gray, cv.COLOR_RGBA2GRAY, 0);
      cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 0);
      let edges = new cv.Mat();
      cv.Canny(gray, edges, 50, 150);

      let M = cv.Mat.ones(3, 3, cv.CV_8U);
      let anchor = new cv.Point(-1, -1);
      cv.dilate(edges, edges, M, anchor, 1, cv.BORDER_CONSTANT, cv.morphologyDefaultBorderValue());
      M.delete();

      let contours = new cv.MatVector();
      let hierarchy = new cv.Mat();
      cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

      let maxArea = 0;
      let globalMaxArea = 0;
      let cardContour: any = null;
      for (let i = 0; i < contours.size(); i++) {
        const cnt = contours.get(i);
        const area = cv.contourArea(cnt);
        if (area > globalMaxArea) globalMaxArea = area;
        if (area < 2000) continue;

        const approx = new cv.Mat();
        cv.approxPolyDP(cnt, approx, 0.04 * cv.arcLength(cnt, true), true);

        if (approx.rows >= 4 && approx.rows <= 9) {
          if (area > maxArea) {
            if (cardContour) cardContour.delete();
            maxArea = area;
            cardContour = approx;
          } else {
            approx.delete();
          }
        } else {
          approx.delete();
        }
      }

      if (Math.random() < 0.05) {
        console.log(`OpenCV: ${contours.size()} contours, Max Blob: ${globalMaxArea.toFixed(0)}, Best Rect: ${maxArea.toFixed(0)}`);
      }

      if (cardContour && maxArea > 3000) {
        console.log("Card detected! Area:", maxArea.toFixed(0));
        setCardDetected(true);
        if (autoCaptureEnabled) {
          setStableCounter((prev) => {
            if (prev >= 18) {
              console.log("Auto-capturing after 3s stability...");
              capture();
              return 0;
            }
            return prev + 1;
          });
        }
        cardContour.delete();
      } else {
        setCardDetected(false);
        setStableCounter(0);
      }

      srcMat.delete();
      gray.delete();
      edges.delete();
      contours.size() && contours.delete();
      hierarchy.delete();
    };
  }, [webcamRef, capture, autoCaptureEnabled]);

  useEffect(() => {
    let interval: number;
    if (isCaptureEnable && !imageStatus && !loading) {
      interval = window.setInterval(() => {
        detectCard();
      }, 200);
    }
    return () => clearInterval(interval);
  }, [isCaptureEnable, imageStatus, loading, detectCard]);

  useEffect(() => {
    if (imageStatus) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const imageToSend = isBase64WithinSizeLimit(imageStatus)
            ? imageStatus
            : await compressBase64Image(imageStatus);

          if (!isBase64WithinSizeLimit(imageToSend)) {
            setMsgToDisplay("Image is too large even after compression. Please retake in better lighting or lower resolution.");
            setOcrStatus(false);
            return;
          }

          const responseOCR = await axios.post(documentOCR_baseURL, {
            file: imageToSend,
          });

          if (responseOCR.data.status !== 1) {
            setMsgToDisplay(responseOCR?.data?.msg || "OCR failed");
            setOcrStatus(false);
            return;
          }

          const ocrResult = responseOCR.data?.data?.result;

          if (title[0] !== ocrResult.documentType) {
            setMsgToDisplay(t('camera.required', { title }));
            setOcrStatus(false);
            return;
          }

          const { issuerOrg_full, nationality_full } = ocrResult;
          const countryMatches = [issuerOrg_full, nationality_full].some(
            field => field?.toLowerCase() === countrySelected.toLowerCase()
          );

          if (!countryMatches) {
            setMsgToDisplay(t('camera.country_mismatch'));
            setOcrStatus(false);
            return;
          }

          const { daysToExpiry } = ocrResult;

          if (daysToExpiry <= 0) {
            setMsgToDisplay(t('camera.expired'));
            setOcrStatus(false);
            return;
          }

          if (daysToExpiry < 30) {
            notifyError(t('camera.expiring_soon'));
          }

          updateSrc(imageToSend);
          setOcrStatus(true);

        } catch (error) {
          console.error("OCR error:", error);
          notifyError("Error processing document");
          setOcrStatus(false);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else {
      setOcrStatus(false);
    }
  }, [imageStatus, countrySelected, title, updateSrc, t]);

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          closeCamera();
          handleClose();
        }}
        ref={ref}
        onExited={() => {
          setImageStatus(null);
          setOcrStatus(false);
          setMsgToDisplay("");
        }}
      >
        {loading && <SpinnerBox />}
        <Modal.Body className={`${theme === "dark" && "bg-black"} bg-sky p-3`}>
          <Form noValidate>
            <Modal.Header
              closeButton
              className="d-flex justify-content-between align-items-center custom-header"
            >
              <label className="form-label fw-bold fs-5 mb-0">
                {side} {title}
              </label>
            </Modal.Header>
            <div className="text-center mt-2 d-flex justify-content-center">
              {isCaptureEnable && (
                <div className="position-relative d-inline-block">
                  {imageStatus ? (
                    <img
                      src={imageStatus}
                      alt="Screenshot"
                      className="rounded-3 img-fluid"
                    />
                  ) : (
                    <>
                      <Webcam
                        audio={false}
                        className="rounded-3 img-fluid d-block"
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        key={facingMode}
                        videoConstraints={{ ...(videoConstraints || {}), facingMode }}
                        onUserMediaError={(error) => {
                          console.error("Webcam error:", error);
                          setCaptureEnable(false);
                          setCameraErrorMsgKey(cameraErrorKey(error));
                        }}
                      />
                      {cardDetected && (
                        <>
                          <div
                            style={{
                              position: "absolute",
                              top: "10%",
                              left: "10%",
                              width: "80%",
                              height: "80%",
                              border: "4px solid #00FF00",
                              borderRadius: "15px",
                              pointerEvents: "none",
                              zIndex: 10,
                              boxShadow: "0 0 30px rgba(0, 255, 0, 0.8), inset 0 0 20px rgba(0, 255, 0, 0.3)"
                            }}
                          />
                          {autoCaptureEnabled && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "15%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                color: "#00FF00",
                                padding: "5px 15px",
                                borderRadius: "20px",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                zIndex: 11
                              }}
                            >
                              {t('camera.stable')}
                            </div>
                          )}
                        </>
                      )}
                      {!(window as any).cv && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            backgroundColor: "rgba(255, 0, 0, 0.6)",
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.7rem",
                            zIndex: 11
                          }}
                        >
                          {t('camera.loading_ai')}
                        </div>
                      )}
                    </>
                  )}
                  {msgToDisplay && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "0",
                        left: "0",
                        width: "100%",
                        backgroundColor: "rgba(220, 53, 69, 0.85)",
                        color: "white",
                        padding: "8px 12px",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                        zIndex: 15
                      }}
                    >
                      {msgToDisplay}
                    </div>
                  )}
                </div>
              )}
            </div>
            {cameraErrorMsgKey ? (
              <div className="text-center mt-4 mb-2 px-3">
                <div className="alert alert-danger small mb-3" role="alert">
                  {t(cameraErrorMsgKey)}
                </div>
                <Button
                  className="btn rounded-5 w-100"
                  variant="outline-secondary"
                  onClick={() => {
                    closeCamera();
                    handleClose();
                  }}
                >
                  {t('common.close')}
                </Button>
              </div>
            ) : (
              <>
            <div className="text-center mt-4">
              <div className="fw-semibold">
                {t('camera.position', { title })}
              </div>
              <div className="text-secondary small">
                {t('camera.make_visible')}
              </div>
            </div>
            <div className="text-center mt-4 mb-2 px-2">
              {imageStatus ? (
                <div className={`d-flex flex-column flex-sm-row ${ocrStatus ? "justify-content-between" : "justify-content-center"} gap-2 w-100 px-3 mt-3`}>
                  <Button
                    className="btn rounded-5 w-100 w-sm-auto"
                    variant="outline-secondary"
                    onClick={() => {
                      setImageStatus(null);
                      setMsgToDisplay(null);
                    }}
                  >
                    <i className="fa-solid fa-rotate-left me-2"></i>
                    {t('camera.retake')}
                  </Button>

                  {ocrStatus && (
                    <Button
                      className="btn btn-primary rounded-5 w-100 w-sm-auto"
                      onClick={() => {
                        setImageStatus(null);
                        closeCamera();
                        handleClose();
                      }}
                    >
                      {t('camera.save_image')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center gap-3">
                   <div className="d-flex flex-column justify-content-center gap-2 w-100 px-3">
                    {isCaptureEnable && hasMultipleCameras && (
                      <Button
                        className="btn rounded-5 text-center w-100"
                        variant="outline-secondary"
                        onClick={toggleFacingMode}
                        style={{
                          fontWeight: "bold",
                        }}
                      >
                        <i className="fa-solid fa-camera-rotate me-2"></i>
                        {t('camera.switch_cam')}
                      </Button>
                    )}
                    <Button
                      className="btn rounded-5 text-center w-100"
                      onClick={capture}
                      style={{
                        backgroundColor: "#00D084",
                        borderColor: "#00D084",
                        color: "#000000",
                        fontWeight: "bold",
                      }}
                    >
                      <i className="fa-solid fa-camera me-2"></i>
                      {t('camera.take_photo')}
                    </Button>
                  </div>
                  <Form.Check
                    type="switch"
                    id="auto-capture-switch"
                    label={autoCaptureEnabled ? t('camera.auto_capture_on') : t('camera.auto_capture_off')}
                    checked={autoCaptureEnabled}
                    onChange={(e) => setAutoCaptureEnabled(e.target.checked)}
                    className="fw-bold"
                  />
                </div>
              )}
            </div>
              </>
            )}
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
});

export default CameraModal;
